var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
export const AspectRatioCropper = (props) => {
    const { image, aspectRatio = 16 / 9, onCropChange, minZoom = 0.5, maxZoom = 3, className = '', } = props;
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const handleWheel = (e) => {
            e.preventDefault();
            const delta = -e.deltaY * 0.001;
            setScale((prev) => Math.max(minZoom, Math.min(maxZoom, prev + delta)));
        };
        if (!containerRef.current) {
            console.error('Forgot to attach ref attribute');
            return;
        }
        containerRef.current.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            var _a;
            (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('wheel', handleWheel);
        };
    }, []);
    useEffect(() => {
        if (!containerRef.current)
            return;
        const updateSize = (width, height) => {
            if (width === 0 || height === 0)
                return;
            setContainerSize({ width, height });
            const imgAspect = image.width / image.height;
            const containerAspect = width / height;
            let displayWidth;
            let displayHeight;
            if (imgAspect > containerAspect) {
                displayWidth = width;
                displayHeight = width / imgAspect;
            }
            else {
                displayHeight = height;
                displayWidth = height * imgAspect;
            }
            setOffset({
                x: (width - displayWidth) / 2,
                y: (height - displayHeight) / 2,
            });
        };
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                updateSize(width, height);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [image]);
    useEffect(() => {
        if (!canvasRef.current || containerSize.width === 0)
            return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        canvas.width = containerSize.width;
        canvas.height = containerSize.height;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const imgAspect = image.width / image.height;
        const baseWidth = containerSize.width;
        const baseHeight = baseWidth / imgAspect;
        const displayWidth = baseWidth * scale;
        const displayHeight = baseHeight * scale;
        ctx.save();
        ctx.drawImage(image, offset.x, offset.y, displayWidth, displayHeight);
        ctx.restore();
        const cropWidth = containerSize.width;
        const cropHeight = cropWidth / aspectRatio;
        const cropY = (containerSize.height - cropHeight) / 2;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        if (cropY > 0) {
            ctx.fillRect(0, 0, containerSize.width, cropY);
        }
        if (cropY + cropHeight < containerSize.height) {
            ctx.fillRect(0, cropY + cropHeight, containerSize.width, containerSize.height - cropY - cropHeight);
        }
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.strokeRect(0, cropY, cropWidth, cropHeight);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        for (let i = 1; i < 3; i++) {
            const x = (cropWidth / 3) * i;
            ctx.beginPath();
            ctx.moveTo(x, cropY);
            ctx.lineTo(x, cropY + cropHeight);
            ctx.stroke();
        }
        for (let i = 1; i < 3; i++) {
            const y = cropY + (cropHeight / 3) * i;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(cropWidth, y);
            ctx.stroke();
        }
        if (onCropChange) {
            const pixelScale = displayWidth / image.width;
            onCropChange({
                x: -offset.x / pixelScale,
                y: (cropY - offset.y) / pixelScale,
                width: cropWidth / pixelScale,
                height: cropHeight / pixelScale,
                scale
            });
        }
    }, [image, scale, offset, containerSize, aspectRatio, onCropChange]);
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };
    const handleMouseMove = (e) => {
        if (!isDragging)
            return;
        setOffset({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y,
        });
    };
    const handleMouseUp = () => {
        setIsDragging(false);
    };
    const handleZoomIn = () => {
        setScale((prev) => Math.min(maxZoom, prev + 0.1));
    };
    const handleZoomOut = () => {
        setScale((prev) => Math.max(minZoom, prev - 0.1));
    };
    const handleReset = () => {
        setScale(1);
        if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            const imgAspect = image.width / image.height;
            let displayWidth;
            let displayHeight;
            if (imgAspect > width / height) {
                displayWidth = width;
                displayHeight = width / imgAspect;
            }
            else {
                displayHeight = height;
                displayWidth = height * imgAspect;
            }
            setOffset({
                x: (width - displayWidth) / 2,
                y: (height - displayHeight) / 2,
            });
        }
    };
    return (_jsxs("div", { className: `aspect-ratio-cropper ${className}`, children: [_jsx("div", { ref: containerRef, className: "cropper-canvas-container", style: {
                    width: '100%',
                    height: '500px',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    backgroundColor: '#000'
                }, onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp, onMouseLeave: handleMouseUp, children: _jsx("canvas", { ref: canvasRef, style: {
                        display: 'block',
                        width: '100%',
                        height: '100%'
                    } }) }), _jsxs("div", { className: "cropper-controls d-flex gap-2 justify-content-center mt-3", children: [_jsxs("button", { type: "button", className: "btn btn-outline-secondary", onClick: handleZoomOut, disabled: scale <= minZoom, children: [_jsx("i", { className: "bi bi-zoom-out" }), " Zoom Out"] }), _jsxs("button", { type: "button", className: "btn btn-outline-secondary", onClick: handleReset, children: [_jsx("i", { className: "bi bi-arrow-clockwise" }), " Reset"] }), _jsxs("button", { type: "button", className: "btn btn-outline-secondary", onClick: handleZoomIn, disabled: scale >= maxZoom, children: [_jsx("i", { className: "bi bi-zoom-in" }), " Zoom In"] })] }), _jsx("div", { className: "text-center mt-2 text-muted", children: _jsxs("small", { children: ["Drag to reposition - Scroll to zoom - ", Math.round(scale * 100), "%"] }) })] }));
};
export const cropImage = (image_1, cropData_1, ...args_1) => __awaiter(void 0, [image_1, cropData_1, ...args_1], void 0, function* (image, cropData, targetWidth = 1920, targetHeight = 1080) {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx)
        throw new Error('Failed to get canvas context');
    const sourceX = Math.max(0, cropData.x);
    const sourceY = Math.max(0, cropData.y);
    ctx.drawImage(image, sourceX, sourceY, Math.min(image.width - sourceX, cropData.width), Math.min(image.height - sourceY, cropData.height), 0, 0, targetWidth, targetHeight);
    return canvas;
});
export const canvasToFile = (canvas_1, filename_1, ...args_1) => __awaiter(void 0, [canvas_1, filename_1, ...args_1], void 0, function* (canvas, filename, mimeType = 'image/jpeg') {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Failed to create blob from canvas'));
                return;
            }
            const file = new File([blob], filename, { type: mimeType });
            resolve(file);
        }, mimeType, 0.95);
    });
});
